export const site = {
  name: "Data Bid Machine",
  shortName: "DBM",
  title: "Data Bid Machine — Digital growth engineered around business outcomes",
  shareTitle: "Data Bid Machine",
  shareDescription:
    "Paid media, organic and AI search, conversion, and measurement connected to the business outcomes that matter.",
  url: "https://databidmachine.com",
  appUrl: "https://app.databidmachine.com",
  signInUrl: "https://app.databidmachine.com/sign-in",
  email: "contact@databidmachine.com",
  careersEmail: "careers@databidmachine.com",
  logo: "https://databidmachine.com/dbm-logo-dark.png",
  twitterHandle: "@databidmachine",
  linkedIn: "https://www.linkedin.com/company/data-bid-machine/",
  twitter: "https://x.com/databidmachine",
  youtube: "https://www.youtube.com/@databidmachine",
  instagram: "https://www.instagram.com/databidmachine",
  facebook: "https://www.facebook.com/databidmachine",
  description:
    "Data Bid Machine connects paid media, organic and AI search, conversion optimization, and measurement to turn digital demand into qualified pipeline, revenue, and applicants.",
  region: "Atlanta HQ · Serving US & UK time zones",
  copyright: "© 2026 Data Bid Machine. All rights reserved.",
  updatedAt: "2026-08-24",
} as const;

export const siteSameAs = [
  site.linkedIn,
  site.twitter,
  site.youtube,
  site.instagram,
  site.facebook,
] as const;

export const navigation = {
  primary: [
    {
      label: "What we do",
      type: "mega" as const,
      key: "services",
    },
    {
      label: "Platform",
      type: "mega" as const,
      key: "platform",
    },
    {
      label: "Verticals",
      type: "mega" as const,
      key: "verticals",
    },
    { label: "How it works", href: "/#how-it-works", type: "link" as const },
    { label: "Case studies", href: "/case-studies", type: "link" as const },
    { label: "Blogs", href: "/blogs", type: "link" as const },
    { label: "About", href: "/company", type: "link" as const },
  ],
};

export type MegaMenu = {
  eyebrow: string;
  title: string;
  subtitle?: string;
  description: string;
  cta: { label: string; href: string };
  groups: {
    label?: string;
    description?: string;
    links: {
      label: string;
      detail: string;
      href: string;
      mark: string;
      badge?: string;
    }[];
  }[];
};

export const megaMenus: Record<"services" | "platform" | "verticals", MegaMenu> = {
  services: {
    eyebrow: "Growth services",
    title: "Senior operators, accountable to growth.",
    description:
      "Strategy and execution across paid media, organic and AI search, conversion, and measurement connected to the business outcomes that matter.",
    cta: { label: "See all services", href: "/services" },
    groups: [
      {
        label: "Search & AI",
        links: [
          {
            label: "AEO visibility",
            detail: "Show up where AI answers and agents find you",
            href: "/services/aeo",
            mark: "01",
          },
          {
            label: "AEO analysis",
            detail: "See how AI engines describe your brand",
            href: "/services/aeo-audit",
            mark: "02",
          },
          {
            label: "SEO management",
            detail: "Classic search, content, and entity readiness",
            href: "/services/seo-management",
            mark: "03",
          },
        ],
      },
      {
        label: "Paid & pipeline",
        links: [
          {
            label: "LinkedIn advertising",
            detail: "Account-based paid reach on LinkedIn",
            href: "/services/linkedin-ads",
            mark: "01",
          },
          {
            label: "Meta advertising",
            detail: "Facebook and Instagram, wired to revenue",
            href: "/services/meta-ads",
            mark: "02",
          },
          {
            label: "ChatGPT advertising",
            detail: "Paid placement inside the answer",
            href: "/services/chatgpt-advertising",
            mark: "03",
          },
          {
            label: "PPC management",
            detail: "Google, Bing, LinkedIn, and Meta",
            href: "/services/ppc-management",
            mark: "04",
          },
          {
            label: "Conversion rate optimization",
            detail: "Turn existing demand into revenue",
            href: "/services/conversion-rate-optimization",
            mark: "05",
          },
          {
            label: "Measurement & Attribution",
            detail: "Tracking, CRM feedback, and clear reporting",
            href: "/services/revenue-measurement",
            mark: "06",
          },
        ],
      },
    ],
  },
  platform: {
    eyebrow: "DBM products",
    title: "Technology built to make better marketing decisions.",
    description:
      "DBM combines senior strategy with proprietary technology that helps our team detect performance changes, uncover growth opportunities, and understand how AI engines see your brand.",
    cta: { label: "See the platform", href: "/platform" },
    groups: [
      {
        label: "DBM PPC Management Software",
        links: [
          {
            label: "KPI Trend Monitor",
            detail: "Detect performance drift and likely causes",
            href: "/platform#kpi-trend-monitor",
            mark: "D",
          },
          {
            label: "Weekly Action Plan",
            detail: "Rank work by impact and confidence",
            href: "/platform#weekly-action-plan",
            mark: "A",
          },
          {
            label: "Budget Monitoring",
            detail: "Control pacing and allocation",
            href: "/platform#budget-monitoring",
            mark: "B",
          },
          {
            label: "Executive Summary",
            detail: "Make account health easy to explain",
            href: "/platform#executive-summary",
            mark: "E",
          },
        ],
      },
      {
        label: "LLM performance monitor",
        links: [
          {
            label: "LLM Performance Monitor",
            detail: "ChatGPT, Gemini, Perplexity, Copilot, and AI Overviews",
            href: "/platform/llm-performance-monitor",
            mark: "L",
            badge: "Coming soon",
          },
          {
            label: "AEO Visibility Program",
            detail: "Win citations in the answers your buyers ask",
            href: "/services/aeo",
            mark: "A",
          },
          {
            label: "Talk to an Expert",
            detail: "Where growth is getting stuck today",
            href: "/growth-gap-audit",
            mark: "?",
          },
        ],
      },
    ],
  },
  verticals: {
    eyebrow: "Verticals",
    title: "Built around how your business grows.",
    description:
      "The same senior-led growth system, adapted to your audience, buying journey, conversion path, and business outcomes.",
    cta: { label: "See all verticals", href: "/verticals" },
    groups: [
      {
        label: "B2B Growth",
        description:
          "Pipeline and revenue programs for software, healthcare, dental, and legal teams where measurable outcomes matter.",
        links: [
          {
            label: "B2B SaaS",
            detail: "Pipeline-led growth for software companies",
            href: "/verticals/b2b-saas",
            mark: "01",
          },
          {
            label: "Healthcare",
            detail: "Compliance-aware paid search",
            href: "/verticals/healthcare",
            mark: "02",
          },
          {
            label: "Dental practices",
            detail: "Patient acquisition and practice growth",
            href: "/verticals/dental-practices",
            mark: "03",
          },
          {
            label: "Legal",
            detail: "Fast launches, disciplined cost",
            href: "/verticals/legal",
            mark: "04",
          },
        ],
      },
      {
        label: "Recruiting & Staffing Growth",
        description:
          "Applicants + placements + client growth—with guardrails for high-volume recruiting auctions.",
        links: [
          {
            label: "Staffing & Recruiting",
            detail: "24/7 guardrails for high-volume auctions",
            href: "/verticals/staffing-recruiting",
            mark: "05",
          },
        ],
      },
    ],
  },
};
